import React, { useEffect, useRef } from 'react';
import {
  FaArrowRight, FaClock, FaMapMarkerAlt, FaMusic, FaBookOpen, FaChild, FaHandsHelping,
} from 'react-icons/fa';
import './Time.css';
import Footer from './Footer';
import img1 from '../Assets/Tus1.jpg';
import img2 from '../Assets/sunday_services.jpg';

const SERVICES = [
  { label: 'First Service', time: '8:00 AM – 10:00 AM', note: 'An earlier start to set your Sunday in worship.', image: img2 },
  { label: 'Second Service', time: '10:30 AM – 12:30 PM', note: 'A later gathering with the same life-giving worship and word.', image: img1 },
];

const EXPECT = [
  { icon: <FaMusic aria-hidden="true" />, title: 'Heartfelt worship', desc: 'A live band and a room full of people lifting their voices together.' },
  { icon: <FaBookOpen aria-hidden="true" />, title: 'Practical teaching', desc: 'Bible-based messages you can carry into your everyday life.' },
  { icon: <FaChild aria-hidden="true" />, title: 'Care for your kids', desc: 'City Kids runs during both services, so children are safe and having fun.' },
  { icon: <FaHandsHelping aria-hidden="true" />, title: 'A warm welcome', desc: 'Our team will greet you, help you settle in and answer any questions.' },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

const Header = () => (
  <div className="ss-head">
    <p className="tcc-eyebrow">Sunday services</p>
    <h2 className="tcc-h2">Two services, one welcome</h2>
    <p className="tcc-lead">Pick the time that suits you best. You will find the same worship and word at both.</p>
  </div>
);

const ServiceTimes = () => {
  const ref = useReveal();

  return (
    <div ref={ref}>
      {/* HERO */}
      <header className="ss-hero">
        <div className="tcc-container ss-hero__inner">
          <p className="tcc-eyebrow ss-hero__eyebrow">Join us this Sunday</p>
          <h1 className="ss-hero__title">Sunday service schedule</h1>
          <p className="ss-hero__lead">
            Whether it is your first time or you have been with us for years, you are in the
            right place. Come and worship, learn and grow with us this Sunday.
          </p>
          <ul className="ss-hero__facts">
            <li><FaClock aria-hidden="true" /> Two services every Sunday</li>
            <li><FaMapMarkerAlt aria-hidden="true" /> The City Church, Luzira</li>
          </ul>
          <div className="ss-hero__actions">
            <a className="tcc-btn tcc-btn--accent tcc-btn--lg" href="#ss-times">
              See the times <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href="/contact-us">
              Plan your visit
            </a>
          </div>
        </div>
      </header>

      {/* SERVICE TIMES */}
      <section id="ss-times" className="tcc-section ss-times reveal" aria-labelledby="ss-times-h" style={{ scrollMarginTop: '80px' }}>
        <div className="tcc-container">
          <Header />
          <div className="ss-grid">
            {SERVICES.map((s) => (
              <article className="ss-card tcc-card tcc-card--hover" key={s.label}>
                <div className="ss-card__media">
                  <img src={s.image} alt={`${s.label} at The City Church`} loading="lazy" />
                  <span className="ss-card__tag">{s.label}</span>
                </div>
                <div className="ss-card__body">
                  <p className="ss-card__time"><FaClock aria-hidden="true" /> {s.time}</p>
                  <p className="ss-card__note">{s.note}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <section className="tcc-section tcc-section--surface2 ss-expect reveal" aria-labelledby="ss-expect-h">
        <div className="tcc-container">
          <div className="ss-head">
            <p className="tcc-eyebrow">Before you come</p>
            <h2 className="tcc-h2" id="ss-expect-h">What to expect</h2>
            <p className="tcc-lead">No pressure, no dress code. Just come as you are.</p>
          </div>
          <ul className="ss-expect__grid">
            {EXPECT.map((e) => (
              <li className="ss-expect__card tcc-card tcc-card--hover" key={e.title}>
                <span className="ss-expect__icon">{e.icon}</span>
                <h3 className="ss-expect__title">{e.title}</h3>
                <p className="ss-expect__desc">{e.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="ss-cta reveal" aria-labelledby="ss-cta-h">
        <div className="tcc-container ss-cta__inner">
          <h2 className="ss-cta__title" id="ss-cta-h">We would love to see you</h2>
          <p className="ss-cta__text">
            We are so excited to have you join us. Our Sunday services are designed to uplift
            your spirit, inspire your heart and deepen your connection with God. Come and
            celebrate his love and grace with us.
          </p>
          <div className="ss-cta__actions">
            <a className="tcc-btn tcc-btn--on-dark tcc-btn--lg" href="/location">
              Get directions <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href="/nextSteps">
              New here? Start here
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export { Header, ServiceTimes };
